import { scrollWithinPane } from '../../shared/lib/scroll-within-pane';

import { captureLearningModel } from './live-context.service';

import type { MioAgentAction } from '@mio/shared';

function waitForElement(
  find: () => HTMLElement | null,
  check?: (element: HTMLElement) => boolean,
  signal?: AbortSignal,
) {
  return new Promise<HTMLElement>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Action interrupted.'));
      return;
    }
    const abort = () => {
      clearTimeout(timeout);
      observer.disconnect();
      reject(new Error('Action interrupted.'));
    };
    const timeout = setTimeout(() => {
      observer.disconnect();
      signal?.removeEventListener('abort', abort);
      reject(new Error('The requested visual did not become ready.'));
    }, 8000);
    const inspect = () => {
      const element = find();
      if (!element) return;
      const failedDiagram = element.querySelector<HTMLElement>('[data-diagram-status="failed"]');
      if (failedDiagram) {
        clearTimeout(timeout);
        observer?.disconnect();
        signal?.removeEventListener('abort', abort);
        const detail = failedDiagram.dataset.diagramError?.slice(0, 400);
        reject(new Error(`The diagram could not render.${detail ? ` ${detail}` : ''}`));
        return;
      }
      if (check && !check(element)) return;
      clearTimeout(timeout);
      observer?.disconnect();
      signal?.removeEventListener('abort', abort);
      resolve(element);
    };
    const observer = new MutationObserver(inspect);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    signal?.addEventListener('abort', abort, { once: true });
    inspect();
  });
}

const lessonReader = () => document.querySelector<HTMLElement>('[data-lesson-id]');

async function mountedTarget(
  reader: HTMLElement,
  key: string,
  block: boolean,
  signal?: AbortSignal,
) {
  const find = () =>
    [...reader.querySelectorAll<HTMLElement>(block ? '[data-learning-block]' : '[id]')].find(
      (element) => (block ? element.dataset.learningBlock === key : element.id === key),
    ) ?? null;
  const existing = find();
  if (existing) return existing;
  const document = reader.querySelector<HTMLElement>('[data-reading-targets]');
  let targets: Record<string, unknown> = {};
  try {
    targets = JSON.parse(document?.dataset.readingTargets ?? '{}') as Record<string, unknown>;
  } catch {
    /* Fail closed. */
  }
  const section = targets[key];
  if (typeof section !== 'string') throw new Error('That section is no longer available.');
  const url = new URL(location.href);
  url.searchParams.set('section', section);
  url.hash = block ? '' : key;
  history.replaceState(history.state, '', url);
  window.dispatchEvent(new Event('mio:reading-navigate'));
  return waitForElement(find, undefined, signal);
}

/** Only the small, validated action vocabulary can affect the page. No arbitrary JS, HTML or URL execution. */
export async function executeMioAction(
  action: MioAgentAction,
  navigate: (path: string) => Promise<void>,
  signal?: AbortSignal,
) {
  signal?.throwIfAborted();
  if (action.type === 'open_lesson' || action.type === 'open_workshop') {
    const environment = action.type === 'open_lesson' ? 'studio' : 'workshop';
    await navigate(
      `/${environment}/${action.activityId}${action.type === 'open_lesson' && action.anchor ? `#${encodeURIComponent(action.anchor)}` : ''}`,
    );
    signal?.throwIfAborted();
    if (action.type === 'open_lesson') {
      const reader = await waitForElement(
        () => {
          const element = lessonReader();
          return element?.dataset.lessonId === action.activityId ? element : null;
        },
        undefined,
        signal,
      );
      if (action.anchor) {
        const target = await mountedTarget(reader, action.anchor, false, signal);
        scrollWithinPane(target, { block: 'start' });
      }
    }
    return;
  }
  if (action.type === 'show_artifact') {
    const artifact = await waitForElement(
      () =>
        [...document.querySelectorAll<HTMLElement>('[data-mio-artifact]')].find(
          (element) => element.dataset.mioArtifact === action.artifactId,
        ) ?? null,
      (element) => !element.querySelector('[data-diagram-status="pending"]'),
      signal,
    );
    scrollWithinPane(artifact, { block: 'nearest' });
    return;
  }
  const reader = lessonReader();
  if (!reader) throw new Error('Open the lesson before using this action.');
  if (action.type === 'select_trace_step') {
    const block = await mountedTarget(reader, action.blockId, true, signal);
    const button = block?.querySelector<HTMLButtonElement>(`[data-trace-step="${action.step}"]`);
    if (!block || !button) throw new Error('That trace step is no longer available.');
    button.click();
    await waitForElement(
      () => block,
      (element) =>
        element.querySelector('[data-trace-index]')?.getAttribute('data-trace-index') ===
        String(action.step),
      signal,
    );
    scrollWithinPane(block, { block: 'nearest' });
    return;
  }
  if (action.type === 'interact_model') {
    const block = await mountedTarget(reader, action.blockId, true, signal);
    const model = captureLearningModel(block);
    const allowed = model?.actions.find((item) => item.id === action.actionId);
    const control = [...block.querySelectorAll<HTMLElement>('[data-model-action]')].find(
      (item) => item.dataset.modelAction === action.actionId,
    );
    if (!allowed || !control || allowed.disabled)
      throw new Error('That model control is unavailable. Refresh the current context.');
    if (
      allowed.kind === 'select' &&
      !allowed.options?.some((option) => option.value === action.value)
    )
      throw new Error('Choose a value from the model control options.');
    if (allowed.kind !== 'button' && action.value === undefined)
      throw new Error('This model control requires a value.');
    const detail = { actionId: action.actionId, value: action.value, handled: false };
    control.dispatchEvent(new CustomEvent('mio:model-action', { bubbles: true, detail }));
    if (!detail.handled) {
      if (allowed.kind === 'button' && control instanceof HTMLButtonElement) control.click();
      else if (control instanceof HTMLInputElement && control.type === 'checkbox') {
        if (action.value !== 'true' && action.value !== 'false')
          throw new Error('A checkbox value must be true or false.');
        if (control.checked !== (action.value === 'true')) control.click();
      } else if (
        control instanceof HTMLSelectElement ||
        control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement
      ) {
        const prototype =
          control instanceof HTMLSelectElement
            ? HTMLSelectElement.prototype
            : control instanceof HTMLTextAreaElement
              ? HTMLTextAreaElement.prototype
              : HTMLInputElement.prototype;
        Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(control, action.value);
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      } else throw new Error('That model control does not support this action.');
    }
    scrollWithinPane(block, { block: 'nearest' });
    return;
  }
  const target = await mountedTarget(reader, action.anchor, false, signal);
  scrollWithinPane(target, { block: 'start' });
  if (action.type === 'highlight') {
    target.classList.add('ring-2', 'ring-mio', 'rounded');
    setTimeout(() => target.classList.remove('ring-2', 'ring-mio', 'rounded'), 4000);
  }
}
